import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoadingBar from "react-top-loading-bar";
import { useIsOnline } from 'react-use-is-online';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import ReactFileReader from 'react-file-reader';
import {
    useDeleteUserLogoMutation,
    useGetUserReceiptSettingsQuery,
    useUserReceiptSettingsMutation
} from '../../Features/ticketsSlice';
import {
    PrinterIcon,
    PhotographIcon,
    ArrowLeftIcon,
    TrashIcon,
    UploadIcon
} from '@heroicons/react/outline';

function CustomizePosReceipt() {
    const navigate = useNavigate();
    const [progress, setProgress] = useState(0);
    const [updateLogo, setUpdateLogo] = useState(false);
    const { isOnline } = useIsOnline();
    const user = useSelector(selectCurrentUser);

    const {
        data: receiptSettings,
        isLoadingSettings,
        isSuccessSettings,
    } = useGetUserReceiptSettingsQuery(user.id);

    const [editReceiptSettings, { isLoading }] =
        useUserReceiptSettingsMutation();

    const [deleteUserLogo] = useDeleteUserLogoMutation();

    const [input, setInput] = useState({
        user_id: user.id,
        header: '',
        footer: '',
        img_url: '',
    });

    const [errors, setErrors] = useState({
        header: '',
        footer: '',
        img_url: '',
    });

    useEffect(() => {
        if (isLoadingSettings) {
            setProgress(80);
        }

        if (isSuccessSettings) {
            setProgress(100);
        } else {
            setProgress(100);
        }

        if (receiptSettings?.data) {
            console.log(receiptSettings?.data);
            setInput(receiptSettings.data);
        }

        return () => {};
    }, [receiptSettings, editReceiptSettings]);

    const handleChange = (e) => {
        setInput({
            ...input,
            [e.target.name]: e.target.value
        });
    };

    const handledeleteLogo = async () => {
        try {
            setProgress(80);

            const resp = await deleteUserLogo(user.id).unwrap();

            if (resp.hasOwnProperty('success') == true) {
                setProgress(100);
                return swal('Successful');
            }
        } catch (err) {
            setProgress(100);

            if (err.data.hasOwnProperty('errors') == true) {
                return swal(
                    "Action Failed",
                    'Please try again later.'
                );
            } else if (err.data.hasOwnProperty('error') == true) {
                setInput({
                    ...input,
                    errorMassage: err.data.error
                });
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                "No Internet connection",
                'Please check Internet connection and try again'
            );
        }

        let data = [];

        if (receiptSettings?.data) {
            data = {
                user_id: user.id,
                header: input.header,
                footer: input.footer,
                img_url: (updateLogo) ? input.img_url : '',
            };
        } else {
            data = input;
        }

        try {
            setProgress(80);

            const resp = await editReceiptSettings(data).unwrap();

            if (resp.hasOwnProperty('success') == true) {
                setProgress(100);
            }
        } catch (err) {
            setProgress(100);

            if (err.data.hasOwnProperty('errors') == true) {
                setErrors({
                    ...errors,
                    header: err.data.errors.header,
                    footer: err.data.errors.footer,
                    img_url: err.data.errors.img_url
                });
            } else if (err.data.hasOwnProperty('error') == true) {
                setInput({
                    ...input,
                    errorMassage: err.data.error
                });
            }
        }
    };

    const handleFile = (file) => {
        setInput({
            ...input,
            img_url: file.base64
        });

        setUpdateLogo(true);
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                "No Internet connection",
                'Please check Internet connection and try again'
            );
        }

        setProgress(100);
        navigate(-1);
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">

                {/* Header */}
                <div className="flex items-center gap-3 mb-6">

                    <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                        <PrinterIcon className="w-6 h-6 text-[#80b643]" />
                    </div>

                    <div>
                        <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                            Customize POS Receipt
                        </h1>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Customize your receipt logo, header and footer
                        </p>
                    </div>

                </div>

                {/* Main Card */}
                <div className="max-w-3xl mx-auto">

                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                        {/* Form Header */}
                        <div className="px-5 sm:px-6 py-5 border-b border-gray-200 dark:border-gray-800">

                            <div className="flex items-center gap-3">

                                <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                    <PrinterIcon className="w-5 h-5 text-[#80b643]" />
                                </div>

                                <div>
                                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                                        Receipt Settings
                                    </h2>

                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        Configure how your POS receipts appear
                                    </p>
                                </div>

                            </div>

                        </div>

                        <form
                            onSubmit={handleSubmit}
                            autoComplete="off"
                        >

                            {/* Receipt Preview / Logo */}
                            <div className="p-5 sm:p-6">

                                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-5">

                                    <div className="flex items-center justify-between mb-5">

                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                Receipt Logo
                                            </h3>

                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Upload a logo to display on printed receipts
                                            </p>
                                        </div>

                                        <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                            <PhotographIcon className="w-5 h-5 text-[#80b643]" />
                                        </div>

                                    </div>

                                    {/* Logo */}
                                    <div className="flex flex-col items-center">

                                        {input.img_url ? (
                                            <div className="relative">

                                                <div className="w-36 h-36 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl flex items-center justify-center overflow-hidden shadow-sm">

                                                    <img
                                                        src={`https://demoapi.autozedltd.com/user_logos/${input.img_url}`}
                                                        alt="Logo"
                                                        className="w-full h-full object-contain p-3"
                                                    />

                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={handledeleteLogo}
                                                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md transition-colors"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>

                                            </div>
                                        ) : (
                                            <div className="w-36 h-36 bg-white dark:bg-gray-900 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">

                                                <PhotographIcon className="w-10 h-10 mb-2" />

                                                <span className="text-xs font-medium">
                                                    No logo
                                                </span>

                                            </div>
                                        )}

                                        <div className="mt-4">

                                            <ReactFileReader
                                                className="flex justify-center items-center cursor-pointer"
                                                multipleFiles={false}
                                                base64={true}
                                                handleFiles={handleFile}
                                            >
                                                <div className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-colors">

                                                    <UploadIcon className="w-4 h-4" />

                                                    <span>
                                                        Upload Logo
                                                    </span>

                                                </div>
                                            </ReactFileReader>

                                        </div>

                                        {errors.img_url && (
                                            <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
                                                {errors.img_url}
                                            </p>
                                        )}

                                    </div>

                                </div>

                                {/* Header */}
                                <div className="mt-6">

                                    <label
                                        htmlFor="header"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                    >
                                        Receipt Header
                                    </label>

                                    <textarea
                                        className={`w-full px-3.5 py-3 h-24 rounded-lg border ${
                                            errors.header
                                                ? 'border-red-300 dark:border-red-700'
                                                : 'border-gray-300 dark:border-gray-700'
                                        } bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm outline-none resize-none focus:ring-2 focus:ring-[#80b643]/20 focus:border-[#80b643] transition`}
                                        placeholder="Enter the text you want to appear at the top of the receipt"
                                        name="header"
                                        id="header"
                                        value={input.header}
                                        onChange={handleChange}
                                    />

                                    {errors.header && (
                                        <p className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                                            {errors.header}
                                        </p>
                                    )}

                                </div>

                                {/* Footer */}
                                <div className="mt-5">

                                    <label
                                        htmlFor="footer"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                    >
                                        Receipt Footer
                                    </label>

                                    <textarea
                                        className={`w-full px-3.5 py-3 h-24 rounded-lg border ${
                                            errors.footer
                                                ? 'border-red-300 dark:border-red-700'
                                                : 'border-gray-300 dark:border-gray-700'
                                        } bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm outline-none resize-none focus:ring-2 focus:ring-[#80b643]/20 focus:border-[#80b643] transition`}
                                        placeholder="Enter the text you want to appear at the bottom of the receipt"
                                        name="footer"
                                        id="footer"
                                        value={input.footer}
                                        onChange={handleChange}
                                    />

                                    {errors.footer && (
                                        <p className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                                            {errors.footer}
                                        </p>
                                    )}

                                </div>

                            </div>

                            {/* Actions */}
                            <div className="px-5 sm:px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-800">

                                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">

                                    <button
                                        type="button"
                                        onClick={back}
                                        className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <ArrowLeftIcon className="w-4 h-4" />
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium shadow-sm transition-colors"
                                    >
                                        {isLoading && (
                                            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                                        )}

                                        {isLoading ? 'Saving...' : 'Save Changes'}
                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>

                </div>

            </div>
        </div>
    );
}

export default CustomizePosReceipt;